import { useState, useEffect, useCallback } from 'react';

import useDebounce from '@lib/core/src/hooks/useDebounce';
import palette from '@lib/core/src/theme/palette';
import CheckIcon from '@mui/icons-material/Check';
import { useQuery } from '@tanstack/react-query';

import { AccountUserDto } from '@/api/generated';
import {
  getListHubspotCompaniesQueryKey,
  ListHubspotCompaniesParams,
  listHubspotCompanies
} from '@/api/generated';
import InputAndAvatars from '@/components/UserSelect/InputAndAvatars';
import { Container, Row, Text } from '@/components/UserSelect/UserSelectStyles';
import UserSelectVariant from '@/components/UserSelect/UserSelectVariant';

import { HubspotCompany } from './types';

interface UserSelectProps {
  companies: HubspotCompany[];
  selectedCompanies: HubspotCompany[];
  setSelectedCompanies: (companies: HubspotCompany[]) => void;
  placeholder?: string;
  variant?: UserSelectVariant;
  disabled?: boolean;
}

const HubspotCompanySelect = ({
  companies,
  selectedCompanies,
  setSelectedCompanies,
  placeholder = 'Type to search for companies...',
  variant = UserSelectVariant.name,
  disabled = false
}: UserSelectProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [isInputInList, setIsInputInList] = useState<boolean>(false);
  const [isInputValid, setIsInputValid] = useState<boolean>(true);
  const [localCompanies, setLocalCompanies] = useState<HubspotCompany[]>([]);
  const debouncedInputValue = useDebounce(inputValue, 500);

  const hubspotCompaniesParams: ListHubspotCompaniesParams = {
    limit: 10,
    offset: 0,
    query: debouncedInputValue
  };

  const { data: companiesFromSearch, isLoading: isLoadingCompaniesFromSearch } = useQuery({
    queryKey: getListHubspotCompaniesQueryKey(hubspotCompaniesParams),
    queryFn: () => listHubspotCompanies(hubspotCompaniesParams),
    enabled: debouncedInputValue.length > 2
  });

  const setOpenEnabled = useCallback(() => {
    if (disabled) return;
    setOpen(prev => !prev);
  }, [disabled]);

  useEffect(() => {
    if (companiesFromSearch?.items) {
      const newLocalCompanies = companiesFromSearch.items.map(({ id, name }) => ({
        id: id || '',
        name: name || ''
      }));
      const uniqueCompanies = [...selectedCompanies, ...newLocalCompanies].filter(
        (company, index, self) => index === self.findIndex(c => c.id === company.id)
      );
      setLocalCompanies(uniqueCompanies);
    }
  }, [companiesFromSearch, selectedCompanies]);

  const onCompaniesSelected = useCallback(
    (event: React.SyntheticEvent, value: HubspotCompany[] | string[] | unknown) => {
      setOpen(true);
      const newSelectedList: HubspotCompany[] = [];
      const newLocalCompaniesList: HubspotCompany[] = [];

      if (!Array.isArray(value)) return;

      (value as HubspotCompany[]).forEach(company => {
        if (typeof company !== 'string') {
          newSelectedList.push(company);
        }
      });

      setLocalCompanies(prev => [...newLocalCompaniesList, ...prev]);
      setSelectedCompanies(newSelectedList);
    },
    [setSelectedCompanies, setLocalCompanies]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, newInputValue: string) => {
      setInputValue(newInputValue);
      if (companies.some(company => company.name?.includes(newInputValue))) {
        setIsInputInList(true);
      }
    },
    [setInputValue, setIsInputInList, companies]
  );

  const sortBySelectedCompanies = (companies: HubspotCompany[]) =>
    companies.sort(
      (a, b) =>
        selectedCompanies.findIndex(company => company.name === b.name) -
        selectedCompanies.findIndex(company => company.name === a.name)
    );

  const smallAvatars = selectedCompanies.slice(0, 4);

  const smallAvatarsWidth = Math.min(selectedCompanies.length * 15, 15 * 5);

  return (
    <Container
      multiple
      freeSolo
      open={open}
      disabled={disabled}
      disableClearable
      getOptionLabel={option => {
        const user = option as AccountUserDto;
        return user ? user.firstName + ' ' + user.lastName : '';
      }}
      onOpen={() => setOpenEnabled()}
      onClose={(event, reason) => {
        if (reason === 'blur' || reason === 'escape') {
          setOpen(false); // Close only if clicked outside or Esc pressed
          setLocalCompanies(prev => sortBySelectedCompanies(prev));
        }
      }}
      options={localCompanies}
      value={selectedCompanies}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={onCompaniesSelected}
      renderTags={() => null}
      filterOptions={(options, { inputValue }) =>
        (options as HubspotCompany[]).filter((option: HubspotCompany) =>
          (option?.name || '').toLowerCase().includes(inputValue.toLowerCase())
        )
      }
      renderInput={params => (
        <InputAndAvatars
          isLoading={isLoadingCompaniesFromSearch}
          smallAvatarsWidth={smallAvatarsWidth}
          smallAvatars={smallAvatars}
          setOpen={setOpenEnabled}
          placeholder={placeholder}
          params={params}
          isInputValid={isInputValid}
          isInputInList={isInputInList}
          selectedUsers={selectedCompanies}
        />
      )}
      renderOption={(props, option, { selected }) => {
        const company = option as HubspotCompany;
        const { key, ...otherProps } = props;
        return (
          <Row key={company.id || key} {...otherProps}>
            <Text>{company.name}</Text>
            {selected && (
              <CheckIcon
                sx={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: palette.blue[100],
                  fontSize: '10px'
                }}
              />
            )}
          </Row>
        );
      }}
    />
  );
};

export default HubspotCompanySelect;
